import {
  apiGetRiskProfileResults,
  apiGetQuestionnaire,
  apiSubmitRiskProfileResults,
  apiGetRiskProfileProgress,
  apiUpsertRiskProfileQuestionProgress,
  apiPublicGetQuestionnaire,
  apiPublicSubmitQuestionnaire,
} from '../../api/riskProfile';

function buildAnswersPayload(orderedQuestions, selectedAnswers) {
  return orderedQuestions
    .map((question, idx) => {
      const selected = selectedAnswers[idx + 1];
      if (!selected) return null;
      const answerIds = Array.isArray(selected) ? selected : [selected];
      if (!answerIds.length) return null;
      return { id: question.id, answers: answerIds };
    })
    .filter(Boolean);
}

function mapResultsToPublic(results) {
  if (!results || !Object.keys(results).length) return null;
  return {
    calculatedScore: results.calculated_score,
    calculatedRiskGroupValue: results.calculated_risk_group_value,
    riskTolerancePercentText: results.risk_tolerance_percent_text,
    financialDecisions: results.financial_decisions,
    individualDifferences: results.individual_differences || [],
    dateCreated: results.date_created || null,
  };
}

export default {
  state: () => ({
    questionnaire: {},
    riskProfileProgress: null,
    riskProfileResults: null,
    publicResult: null,
    selectedAnswers: {},
    updatePromise: null,
    questionnaireLoaded: false,
  }),

  getters: {
    questionCount(state) {
      return state.questionnaire?.questions?.length || 0;
    },

    orderedQuestions(state) {
      if (state.questionnaire?.questions) {
        const sorted = state.questionnaire.questions
          .map((question) => ({
            ...question,
            options: question.options.sort(
              (a, b) => a.displayOrder - b.displayOrder,
            ),
          }))
          .sort((a, b) => a.displayOrder - b.displayOrder);
        return sorted;
      }
      return [];
    },

    enableSaveProgress(state, getters, rootState) {
      return !rootState.user.isSubscriptionGMA;
    },
  },

  mutations: {
    setQuestionnaireLoaded(state, payload) {
      state.questionnaireLoaded = payload;
    },

    setSelectedAnswers(state, payload) {
      if (!payload) {
        state.selectedAnswers = {};
        return;
      }

      const { question, selected } = payload;
      state.selectedAnswers = {
        ...state.selectedAnswers,
        [question]: selected,
      };
    },

    setQuestionnaire(state, payload) {
      if (payload) {
        state.questionnaireLoaded = true;
      }
      state.questionnaire = payload;
    },

    setRiskProfileProgress(state, payload) {
      if (!payload) {
        state.riskProfileProgress = null;
        return;
      }
      const { questionnaire_id: questionnaireId, questions: answers } = payload;
      if (state.questionnaire?.questions) {
        (answers || []).forEach((answer) => {
          if (!answer) {
            return;
          }
          const question = state.questionnaire.questions.find((q) => q.id === answer.id);
          if (!question) {
            return;
          }
          if (question.multipleSelect) {
            state.selectedAnswers[question.displayOrder] = answer.answers;
          } else {
            const [ans] = answer.answers;
            state.selectedAnswers[question.displayOrder] = ans;
          }
        });
      }
      state.riskProfileProgress = {
        questionnaireId,
        answers,
      };
    },

    setRiskProfileResults(state, payload) {
      state.riskProfileResults = payload;
    },

    setPublicResult(state, result) {
      state.publicResult = result;
    },

    addRiskProfileProgress(state, payload) {
      if (!state.riskProfileProgress) {
        state.riskProfileProgress = {
          questionnaireId: state.questionnaire.id,
          answers: [payload],
        };
        return;
      }
      let found = false;
      const answers = state.riskProfileProgress.answers.map((answer) => {
        if (answer.id === payload.id) {
          found = true;
          return payload;
        }
        return answer;
      });
      if (!found) {
        answers.push(payload);
      }

      state.riskProfileProgress = {
        ...state.riskProfileProgress,
        answers,
      };
    },

    setUpdatePromise(state, payload) {
      state.updatePromise = payload;
    },
  },

  actions: {
    async loadQuestionnaire({ commit }, payload) {
      try {
        const { results } = await apiGetQuestionnaire({ questionnaireId: payload });
        if (results) commit('setQuestionnaire', results);
      } catch (e) {
        commit('setQuestionnaire', {});
      }
    },

    async loadRiskProfileProgress({ commit }) {
      try {
        const results = await apiGetRiskProfileProgress();
        if (results) commit('setRiskProfileProgress', results);
      } catch (e) {
        commit('setRiskProfileProgress', null);
      }
    },

    async loadRiskProfileResults({ commit }) {
      try {
        const results = await apiGetRiskProfileResults();
        if (results) commit('setRiskProfileResults', results);
      } catch (e) {
        commit('setRiskProfileResults', null);
      }
    },

    async submitRiskProfileResults({ commit, state, rootGetters }) {
      const enableSaveProgress = rootGetters['riskProfile/enableSaveProgress'];
      if (state.updatePromise) {
        try {
          await state.updatePromise;
        } catch (e) {
          // ignore errors
        }
        commit('setUpdatePromise', null);
      }
      try {
        if (enableSaveProgress) {
          const { results } = await apiSubmitRiskProfileResults();
          if (results) commit('setRiskProfileResults', results);
        } else {
          const { questionnaireId, answers } = state.riskProfileProgress;
          const { results } = await apiSubmitRiskProfileResults({ questionnaireId, answers });
          if (results) commit('setRiskProfileResults', results);
        }
      } catch (e) {
        // ignore
      }
    },

    async updateRiskProfileProgress({ commit, rootGetters }, payload) {
      const enableSaveProgress = rootGetters['riskProfile/enableSaveProgress'];
      commit('addRiskProfileProgress', payload);
      if (enableSaveProgress) {
        const promise = apiUpsertRiskProfileQuestionProgress(payload);
        commit('setUpdatePromise', promise);
        try {
          await promise;
        } catch (e) {
          // ignore
        }
        commit('setUpdatePromise', null);
      }
    },

    async loadPublicQuestionnaire({ commit }) {
      try {
        const { results } = await apiPublicGetQuestionnaire();
        if (results) commit('setQuestionnaire', results);
      } catch (e) {
        commit('setQuestionnaire', {});
      }
    },

    async submitPublicResults(
      { commit, state },
      {
        firstName, lastName, phone, email, selectedAnswers, orderedQuestions,
      },
    ) {
      const answers = buildAnswersPayload(orderedQuestions, selectedAnswers);
      const questionnaireId = state.questionnaire?.id;
      const { results } = await apiPublicSubmitQuestionnaire({
        first_name: firstName,
        last_name: lastName,
        phone_number: phone,
        email,
        answers,
        questionnaire_id: questionnaireId,
      });
      commit('setPublicResult', results);
    },

    async loadAppResults({ commit }) {
      try {
        const results = await apiGetRiskProfileResults();
        if (results && Object.keys(results).length) {
          commit('setRiskProfileResults', results);
          commit('setPublicResult', mapResultsToPublic(results));
        }
      } catch (e) {
        commit('setRiskProfileResults', null);
      }
    },

    async submitAppQuestionnaire({ commit, state }, { selectedAnswers, orderedQuestions }) {
      const answers = buildAnswersPayload(orderedQuestions, selectedAnswers);
      const questionnaireId = state.questionnaire?.id;
      const { results } = await apiSubmitRiskProfileResults({ questionnaireId, answers });
      if (results) {
        commit('setRiskProfileResults', results);
        commit('setPublicResult', mapResultsToPublic(results));
      }
    },
  },

  namespaced: true,
};
